import { AlertTriangleIcon, CheckCircle2Icon, ChevronDownIcon, HeartIcon, InfoIcon, MessageSquareIcon, MoreVerticalIcon, Trash2Icon } from "lucide-react";
import { Applicant, ApplicantJobInsight, InterviewInsightItem } from "@type/applicant";
import { formatHumanReadable } from "@util/date";
import { useEffect, useState } from "react";
import { getCookie } from "@util/cookie";
import { useUserProfileContext } from "@context/user_profile";
import { ShortlistedCandidateItem } from "@type/candidate";
import { updateApplicantInfo } from "@service/user";
import { applicantUpdateStatus } from "@service/job";
import { Job } from "@type/job";
import { Tooltip } from "./tooltip";
import { Badge } from "./badge";
import { cn } from "@util/tailwind";
import { Checkbox } from "./checkbox";
import { getJobApplicantInsight } from "@service/job";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { getMatchBadge } from "@util/job";
import { convertCapitalizeWords, convertToLowerSnakeCase, getApplicantStatusBadge } from "@util/string";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu";
import DropdownApplicantStatus from "./dropdown-applicant-status";
import ShortlistToInviteDialog from "@container/dialog/dialog-shortlist-invite";
import InlineEditableField from "./inline-editable-field";
import PhoneInputWithCountry from "./phone-input-with-country";
import DialogScoreBreakdown from "@container/dialog/dialog-score-breakdown";

interface Props {
    job: Job | null
    applicant: Applicant
    shortlistedCount: number
    commentCount?: number
    onSelectedApplicant: (applicantID: string, selected: boolean) => void
    onViewProfile: () => void
    onViewReview?: () => void
    onUpdateScoreSimilarity: (applicantID: string, score: number) => void;
    onViewCV: (applicant: Applicant) => void;
    onReload: () => void;
    onApplicantStatusChange?: (applicantID: string, status: string) => void;
    onDeleteCandidate?: (applicant: Applicant) => void;
}

export default function UploadApplicantItem({ job, applicant, shortlistedCount, commentCount = 0, onSelectedApplicant, onViewProfile, onViewReview, onUpdateScoreSimilarity, onViewCV, onReload, onApplicantStatusChange, onDeleteCandidate }: Props) {
    const snackbar = useSnackbar();
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const [matchPercentage, setMatchPercentage] = useState<number | string>(applicant.similarity_score === "-" ? "-" : Number(applicant.similarity_score));
    const [shortlistDialogOpen, setShortlistDialogOpen] = useState<boolean>(false);
    const [isOpenDialogScoreBreakdown, setIsOpenDialogScoreBreakdown] = useState<boolean>(false);
    const [isShortlisted, setIsShortlisted] = useState<boolean>(false);
    const [isLoadingGenerateInsight, setIsLoadingGenerateInsight] = useState<boolean>(false);
    const [applicantInsight, setApplicantInsight] = useState<ApplicantJobInsight | undefined>(undefined);
    const [collapsedInsights, setCollapsedInsights] = useState<boolean>(true);

    useEffect(() => {
        if (applicant && currentUserInfo) {
            const shortlistedCandidates = getCookie(`shortlisted_candidate:${currentUserInfo?.id}`);
            if (shortlistedCandidates) {
                const exists = Boolean((shortlistedCandidates[applicant.applicant_id as keyof ShortlistedCandidateItem] || []).find((item: ShortlistedCandidateItem) => item.job_id === job?.id));
                setIsShortlisted(exists);
            } else {
                setIsShortlisted(false);
            }
        }
    }, [applicant, currentUserInfo])

    useEffect(() => {
        const checkApplicantInsight = JSON.parse(sessionStorage.getItem(`applicant:insight:${job?.id}:${applicant?.id}`) || "null");
        if (checkApplicantInsight) {
            setApplicantInsight(checkApplicantInsight);
            setIsLoadingGenerateInsight(false);
            return;
        }

        setIsLoadingGenerateInsight(true);
        getJobApplicantInsight(job?.id || "", applicant?.applicant_id || "").then((resp) => {
            if (resp.status === 200) {
                setApplicantInsight(resp.data.data);
                sessionStorage.setItem(`applicant:insight:${job?.id}:${applicant?.id}`, JSON.stringify(resp.data.data || []));
            }
        }).finally(() => {
            setIsLoadingGenerateInsight(false);
        });
    }, [])

    useEffect(() => {
        if (applicant.similarity_score !== "-") {
            setMatchPercentage(Number(applicant.similarity_score));
            onUpdateScoreSimilarity(applicant.applicant_id, Number(applicant.similarity_score));
        }
    }, [applicant.similarity_score])


    const onSaveApplicantField = (field: string, newValue: string) => {
        updateApplicantInfo(applicant.applicant_id, {
            ...{
                name: (applicant.cv_data?.CV_data?.name || applicant.applicant_name || ""),
                ...(applicant.cv_data?.CV_data?.email ? { email: applicant.cv_data?.CV_data?.email } : {}),
                country_code: applicant.country_code,
                phone_number: (applicant.cv_data?.CV_data?.phone || applicant.phone_number || ""),
                job_id: job?.id || "",
            },
            [field]: newValue,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully.`);
                onReload();
            }
        }).catch((error) => {
            catchErrorLog(`Failed to update applicant ${field}: ${error}`);
            snackbar.showError(`Failed to update ${field}. Please try again.`);
        })
    }

    const onSavePhoneField = (newCountryCode: string, newPhone: string) => {
        updateApplicantInfo(applicant.applicant_id, {
            ...{
                name: (applicant.cv_data?.CV_data?.name || applicant.applicant_name || ""),
                ...(applicant.cv_data?.CV_data?.email ? { email: applicant.cv_data?.CV_data?.email } : {}),
                country_code: applicant.country_code,
                phone_number: (applicant.cv_data?.CV_data?.phone || applicant.phone_number || ""),
                job_id: job?.id || "",
            },
            country_code: newCountryCode,
            phone_number: newPhone,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Phone number updated successfully.");
                onReload();
            }
        }).catch((error) => {
            catchErrorLog(`Failed to update applicant phone number: ${error}`);
            snackbar.showError("Failed to update phone number. Please try again.");
        })
    }

    const handleStatusChange = (newStatus: string) => {
        if (newStatus === convertToLowerSnakeCase(applicant.status)) return;
        applicantUpdateStatus(job?.id || "", {
            user_id: applicant.applicant_id,
            status: convertCapitalizeWords(newStatus),
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`Status updated to ${getApplicantStatusBadge(newStatus).label}.`);
                onApplicantStatusChange?.(applicant.applicant_id, newStatus);
            }
        }).catch((error) => {
            catchErrorLog(`failed to update upload applicant status: ${error}`);
            snackbar.showError("Failed to update applicant status. Please try again.");
        });
    };

    return (
        <>
            <tr key={applicant.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer" onClick={onViewProfile}>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div
                        className="flex items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            checked={applicant.is_selected || false}
                            onCheckedChange={(checked) =>
                                onSelectedApplicant(applicant.applicant_id, Boolean(checked))
                            }
                            aria-label={`Select ${applicant.applicant_name}`}
                        />
                    </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                    <div className="w-full flex flex-col">
                        <InlineEditableField
                            value={(applicant.cv_data?.CV_data?.name || applicant.applicant_name || "")}
                            onSave={(newValue: string) => onSaveApplicantField("name", newValue)}
                            type="text"
                            placeholder="Enter name"
                            className="font-medium"
                        />
                        <Tooltip
                            content={applicant.filename}
                        >
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewCV(applicant);
                                }}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline mt-0.5 cursor-pointer text-left max-w-[200px] block"
                            >
                                {applicant.filename && applicant.filename.length > 20
                                    ? `${applicant.filename.slice(0, applicant.filename.length - 6).slice(0, 14)}...${applicant.filename.slice(-6)}`
                                    : applicant.filename}
                            </button>
                        </Tooltip>
                    </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-gray-500 dark:text-gray-400">
                    <PhoneInputWithCountry
                        value={(applicant.cv_data?.CV_data?.phone || applicant.phone_number || "")}
                        countryCode={applicant.country_code || "+62"}
                        onSave={(newCountryCode: string, newPhone: string) => {
                            onSavePhoneField(newCountryCode, newPhone);
                        }}
                        maxWidth="200px"
                        placeholder="+ Add Phone"
                    />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-gray-500 dark:text-gray-400">
                    <InlineEditableField
                        value={applicant.cv_data?.CV_data?.email}
                        onSave={(newValue: string) => onSaveApplicantField("email", newValue)}
                        type="email"
                        placeholder="+ Add Email"
                    />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                    {formatHumanReadable(applicant.created_at, true)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                    <DropdownApplicantStatus
                        currentStatus={convertToLowerSnakeCase(applicant.status)}
                        onStatusChange={handleStatusChange}
                        isIntakeTab={true}
                    />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    {matchPercentage === "-" ? (
                        <div className="flex justify-center items-center">
                            <svg className="animate-spin h-4 w-4 text-gray-800 mt-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : (
                        <Tooltip content={(
                            <p>View detailed scoring breakdown</p>
                        )}>
                            <div
                                className={`inline-block cursor-pointer hover:opacity-80 transition-opacity`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpenDialogScoreBreakdown(true)
                                }}
                            >
                                <Badge
                                    className={cn(getMatchBadge(
                                        matchPercentage as number,
                                    ),
                                        "inline-flex items-center gap-1.5"
                                    )}
                                    style={{
                                        paddingTop: "4px",
                                        paddingBottom: "4px",
                                        paddingLeft: "8px",
                                        paddingRight: "8px",
                                        border:
                                            "0.909091px solid rgba(0, 0, 0, 0)",
                                        borderRadius: "1rem",
                                        boxShadow: "none",
                                    }}
                                >
                                    {Number(matchPercentage) || 0}% match
                                    <InfoIcon className="w-3.5 h-3.5" />
                                </Badge>
                            </div>
                        </Tooltip>
                    )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                    {!isLoadingGenerateInsight ? (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCollapsedInsights(!collapsedInsights);
                                }}
                                className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                            >
                                <ChevronDownIcon
                                    className={cn(
                                        "w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform",
                                        collapsedInsights && "-rotate-90"
                                    )}
                                />
                            </button>
                            <div className="flex-1">
                                {!collapsedInsights && (
                                    <div className="space-y-1">
                                        {(applicantInsight?.insights || []).map((insight: InterviewInsightItem, index: number) => (
                                            <div key={index} className={cn("flex items-center gap-1.5 text-xs", {
                                                "text-green-700": insight.type === "positive",
                                                "text-amber-700": insight.type === "negative",
                                            })}>
                                                {insight.type === "positive" ? (
                                                    <CheckCircle2Icon className="w-3.5 h-3.5 flex-shrink-0" />
                                                ) : (
                                                    <AlertTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                                )}
                                                <span>{insight.insight}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {collapsedInsights && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {(applicantInsight?.insights || []).filter((i) => i.type === "positive").length > (applicantInsight?.insights || []).filter((i) => i.type === "negative").length
                                            ? `${(applicantInsight?.insights || []).filter((i) => i.type === "positive").length} strengths`
                                            : (applicantInsight?.insights || []).filter((i) => i.type === "positive").length < (applicantInsight?.insights || []).filter((i) => i.type === "negative").length
                                                ? `${(applicantInsight?.insights || []).filter((i) => i.type === "negative").length} skill gaps`
                                                : (applicantInsight?.insights || []).filter((i) => i.type === "positive").length === 2 && (applicantInsight?.insights || []).filter((i) => i.type === "negative").length === 2
                                                    ? `${(applicantInsight?.insights || []).filter((i) => i.type === "positive").length} strengths and ${(applicantInsight?.insights || []).filter((i) => i.type === "negative").length} skill gaps`
                                                    : `${(applicantInsight?.insights || []).length} insights`}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center items-center">
                            <svg className="animate-spin h-4 w-4 text-gray-800 mt-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    )}

                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center">
                        <Tooltip content={(
                            <p>
                                {shortlistedCount > 0
                                    ? `${shortlistedCount} member${shortlistedCount !== 1 ? "s have" : " has"} shortlisted this applicant`
                                    : "Shortlist this applicant"}
                            </p>
                        )}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShortlistDialogOpen(true);
                                }}
                                className="flex items-center gap-1 p-1 rounded-full transition-colors group"
                            >
                                <HeartIcon className={cn("w-5 h-5 transition-colors", {
                                    "text-pink-500 fill-pink-500": isShortlisted,
                                    "text-gray-300 group-hover:text-pink-400 group-hover:fill-pink-400": !isShortlisted,
                                })} />
                                <span className={cn("text-sm font-medium text-pink-500 w-4 text-left leading-none",
                                    shortlistedCount === 0 && "invisible")}>
                                    {shortlistedCount}
                                </span>
                            </button>
                        </Tooltip>
                        {onViewReview && (
                            <Tooltip content={<p>Leave a comment</p>}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onViewReview();
                                    }}
                                    className="flex items-center gap-1 p-1 rounded-full transition-colors group"
                                    aria-label="Open comments"
                                >
                                    <MessageSquareIcon className={cn("w-5 h-5 transition-colors", commentCount > 0 ? "text-blue-500 fill-blue-500" : "text-gray-300 group-hover:text-blue-500 group-hover:fill-blue-500")} />
                                    <span className={cn("text-sm font-medium text-blue-500 w-4 text-left leading-none",
                                        commentCount === 0 && "invisible")}>
                                        {commentCount}
                                    </span>
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </td>
                <td className="px-2 py-4 whitespace-nowrap text-center">
                    <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 cursor-pointer">
                                    <MoreVerticalIcon className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                    onClick={() => onDeleteCandidate?.(applicant)}
                                    className="text-red-600 focus:text-red-600 cursor-pointer"
                                >
                                    <Trash2Icon className="w-4 h-4 mr-2" />
                                    <span className="text-sm">Delete candidate</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </td>
            </tr >

            <ShortlistToInviteDialog
                candidate_id={applicant.applicant_id || ""}
                candidate_name={(applicant.cv_data?.CV_data?.name || applicant.applicant_name || "")}
                isOpen={shortlistDialogOpen}
                selectedJob={job}
                onClose={() => {
                    setShortlistDialogOpen(false);
                }}
            />

            <DialogScoreBreakdown
                isOpen={isOpenDialogScoreBreakdown}
                onClose={() => setIsOpenDialogScoreBreakdown(false)}
                applicant={applicant}
                candidate_id={applicant.applicant_id}
                candidate_name={(applicant.cv_data?.CV_data?.name || applicant.applicant_name || "")}
                match_score={matchPercentage as number}
                job_id={job?.id || ""}
                job_title={job?.job_title || ""}
                applied_date={applicant.created_at}
                assessment_ids={job?.assessment_ids}
            />
        </>
    )
}